import type { IAvailableFilter, IFilterMetadata } from '@/types';

type FilterPanelEntry = Pick<IFilterMetadata, 'title' | 'forApiOnly'>;

export type { FilterPanelEntry };

function isPanelFilter(meta: FilterPanelEntry): boolean {
    return !meta.forApiOnly;
}

export function useFilterPanelState<T extends Record<string, FilterPanelEntry>>(filters: T) {
    const hiddenFilters = ref<string[]>([]);
    const visibleFiltersMobile = ref<string[]>([]);
    const filterOrder = ref<string[]>([]);
    const useButtonForFiltersApply = ref(false);
    const saveFiltersState = ref(false);

    const panelFilterKeys = computed(() => Object.entries(filters).filter(([, meta]) => isPanelFilter(meta)).map(([key]) => key));

    const availableFilters = computed<IAvailableFilter[]>(() => {
        const entries = Object.entries(filters)
            .filter(([, meta]) => isPanelFilter(meta))
            .map(([key, meta]) => ({
                key,
                title: meta.title || '',
            }));

        if (filterOrder.value.length > 0) {
            entries.sort((a, b) => {
                const indexA = filterOrder.value.indexOf(a.key);
                const indexB = filterOrder.value.indexOf(b.key);
                return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
            });
        }

        return entries;
    });

    const visibleFilters = computed(() => panelFilterKeys.value.filter((key) => !hiddenFilters.value.includes(key)));

    return {
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
    };
}

export type FilterPanelState = {
    hiddenFilters: Ref<string[]>;
    visibleFiltersMobile: Ref<string[]>;
    filterOrder: Ref<string[]>;
    availableFilters: ComputedRef<IAvailableFilter[]>;
    useButtonForFiltersApply: Ref<boolean>;
    saveFiltersState: Ref<boolean>;
    visibleFilters: ComputedRef<string[]>;
};
